import React from 'react'
import { usePushNotifications, type PushPlatform, type PushState } from '../../hooks/usePushNotifications'
import { useNotificationStatus, useSendTestNotification } from '../../api/notifications'
import { useToast } from '../primitives/Toast'

const bodyStyle: React.CSSProperties = {
  fontFamily: 'var(--font-serif)',
  fontSize: '0.8125rem',
  color: 'var(--color-ink-faint)',
  marginBottom: '10px',
  lineHeight: 1.5,
}

function toggleStyle(active: boolean, disabled: boolean): React.CSSProperties {
  return {
    padding: '12px 16px',
    borderRadius: 'var(--radius-md)',
    border: active ? '1px solid var(--color-accent)' : '1px solid var(--color-border)',
    background: active ? 'var(--color-paper-dark)' : 'var(--color-surface)',
    cursor: disabled ? 'default' : 'pointer',
    fontFamily: 'var(--font-serif)',
    fontSize: '0.875rem',
    color: active ? 'var(--color-ink)' : 'var(--color-ink-muted)',
    fontWeight: active ? 600 : 400,
    opacity: disabled ? 0.6 : 1,
  }
}

const linkButtonStyle: React.CSSProperties = {
  marginTop: '12px',
  padding: '8px 14px',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--color-border)',
  background: 'var(--color-surface)',
  cursor: 'pointer',
  fontFamily: 'var(--font-serif)',
  fontSize: '0.8125rem',
  color: 'var(--color-ink)',
}

// Browsers give no programmatic way back from Block, so the only honest thing
// to show is where the setting lives on each platform.
const UNBLOCK_STEPS: Record<PushPlatform, string> = {
  desktop: 'Click the lock icon in the address bar → Site settings → Notifications → Allow.',
  android: 'Long-press the todux icon → App info → Notifications → allow.',
  ios: 'Settings → Notifications → todux → Allow Notifications.',
}

export function NotificationsSection() {
  const push = usePushNotifications()
  const { data: status } = useNotificationStatus()
  const sendTest = useSendTestNotification()
  const { showToast } = useToast()

  const state: PushState = status && !status.configured && push.state !== 'unsupported'
    ? 'unconfigured'
    : push.state

  const handleTest = () => {
    sendTest.mutate(undefined, {
      onSuccess: (res) =>
        showToast(
          res.delivered > 0
            ? `Test sent to ${res.delivered} device${res.delivered === 1 ? '' : 's'}.`
            : 'No device received the test notification.',
          res.delivered > 0 ? 'success' : 'error'
        ),
      onError: () => showToast('Could not send the test notification.', 'error'),
    })
  }

  return (
    <div>
      <p style={bodyStyle}>
        Reminders you set on a task are delivered as push notifications to every device you turn
        this on for.
      </p>

      {state === 'unsupported' && (
        <p style={bodyStyle}>This browser can&rsquo;t receive push notifications.</p>
      )}

      {state === 'unconfigured' && (
        <p style={bodyStyle}>Push isn&rsquo;t configured on this server.</p>
      )}

      {state === 'needs-install' && (
        <p style={bodyStyle}>
          iOS only delivers notifications to an installed app. Tap Share → Add to Home Screen, then
          open todux from the home screen and come back here.
        </p>
      )}

      {(state === 'default' || state === 'granted-unsubscribed' || state === 'subscribed') && (
        <div style={{ display: 'flex', gap: '10px' }}>
          {([true, false] as const).map((on) => {
            const active = push.enabled === on
            return (
              <button
                key={String(on)}
                onClick={() => void (on ? push.subscribe() : push.unsubscribe())}
                disabled={push.isBusy}
                style={toggleStyle(active, push.isBusy)}
              >
                {push.isBusy ? '…' : on ? 'On' : 'Off'}
              </button>
            )
          })}
        </div>
      )}

      {state === 'denied' && (
        <div>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
            <button disabled style={toggleStyle(false, true)}>On</button>
            <button disabled style={toggleStyle(true, true)}>Off</button>
          </div>
          <p style={bodyStyle}>
            Notifications are blocked for todux. {UNBLOCK_STEPS[push.platform]}
          </p>
          <button onClick={() => void push.recheck()} style={linkButtonStyle} disabled={push.isBusy}>
            Check again
          </button>
        </div>
      )}

      {push.enabled && (
        <div style={{ marginTop: '12px' }}>
          <p style={bodyStyle}>
            This device will receive reminders.
            {status && status.active_subscriptions > 0 && (
              <> {status.active_subscriptions} device
                {status.active_subscriptions === 1 ? '' : 's'} registered.</>
            )}
          </p>
          <button onClick={handleTest} style={linkButtonStyle} disabled={sendTest.isPending}>
            {sendTest.isPending ? 'Sending…' : 'Send test notification'}
          </button>
        </div>
      )}

      {push.error && (
        <p style={{ ...bodyStyle, color: '#C0392B', marginTop: '10px' }}>{push.error}</p>
      )}
    </div>
  )
}
